import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../../../../utils/apiConfig";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import BankingIntegrationModal from "./expenses-budgets/BankingIntegrationModal";
import DebtAnalysisTable from "../../../shared/DebtAnalysisTable";
import ExpenseRow from "./expenses-budgets/ExpenseRow";
import ExpenseCharts from "./expenses-budgets/ExpenseCharts";

import logger from '../../../../utils/logger';

// Utility to format numbers as currency
function formatCurrency(value) {
  if (value === "" || value === undefined || value === null) return "";
  const floatVal = parseFloat(value);
  if (isNaN(floatVal)) return "";
  return floatVal.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

// Initial state
const initialState = {
  loading: true,
  error: null,
  totalIncome: 0,
  totalExpenses: 0,
  dateRange: "monthly",
  fixedExpenses: [],
  discretionaryExpenses: [],
  hasDebts: true,
  showBankingPrompt: true,
  integrationStatus: "none",
  showIntegrationModal: false,
  expenseFilter: "all",
  expenseTypes: [],
  expenseCategories: [],
  frequencies: [],
  currentExpenses: [],
  historyExpenses: [],
  incomeHistory: [],
};

// Helper to get token
const getToken = () => localStorage.getItem("token");

// Helper to normalize to selected view
function normalizeToView(amount, frequency_multiplier, dateRange) {
  if (!amount || !frequency_multiplier) return 0;
  let divisor = 12;
  if (dateRange === "quarterly") divisor = 4;
  if (dateRange === "yearly") divisor = 1;
  const result =
    (parseFloat(amount) * parseFloat(frequency_multiplier)) / divisor;
  // Debug log for tracing
  logger.info(`[Category Math]`, {
    amount,
    frequency_multiplier,
    dateRange,
    divisor,
    result,
  });
  return result;
}

// Helper to get debt divisor
function getDebtDivisor(dateRange) {
  if (dateRange === "quarterly") return 3;
  if (dateRange === "yearly") return 12;
  return 1; // monthly
}

// 1. Add range options
const RANGE_OPTIONS = [
  { label: "6 Months", value: "6m" },
  { label: "1 Year", value: "1y" },
  { label: "3 Years", value: "3y" },
  { label: "Since Inception", value: "all" },
];

// Helper to get months between two dates (inclusive)
function getMonthRange(start, end) {
  const months = [];
  const date = new Date(start.getFullYear(), start.getMonth(), 1);
  while (date <= end) {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    months.push(key);
    date.setMonth(date.getMonth() + 1);
  }
  // Debug: print all generated months
  logger.info("getMonthRange:", months);
  return months;
}

// Helper to add months to a date
function addMonthsToDate(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

// Helper to get all occurrence months for an expense
function getExpenseOccurrences(expense, monthsArr) {
  const occurrences = [];
  // Use created_at as fallback for start_date
  const start = new Date(
    expense.start_date || expense.created_at || "2022-01-01",
  );
  let end;
  if (expense.end_date) {
    end = new Date(expense.end_date);
  } else if (expense.updated_at && expense.expense_id) {
    end = new Date(expense.updated_at);
  } else {
    end = new Date();
  }
  if (isNaN(start.getTime()) || !expense.amount) return occurrences;
  const freq = (
    expense.frequency ||
    expense.frequency_name ||
    ""
  ).toLowerCase();
  let interval = 1;
  if (freq.includes("quarter")) interval = 3;
  else if (freq.includes("annual") || freq.includes("year")) interval = 12;
  // Use UTC for all date math
  let current = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1),
  );
  const utcEnd = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  while (current <= utcEnd) {
    const key = `${current.getUTCFullYear()}-${String(current.getUTCMonth() + 1).padStart(2, "0")}`;
    if (monthsArr.includes(key)) {
      occurrences.push(key);
    }
    // Debug: print every generated key for a sample expense
    if (
      expense.notes &&
      expense.notes.includes("SYNTHETIC_TEST") &&
      (current.getUTCFullYear() === 2022 || current.getUTCFullYear() === 2023)
    ) {
      logger.info(
        "Expense",
        expense.client_expense_id,
        "loop month:",
        key,
        "current:",
        current.toISOString(),
      );
    }
    current = new Date(
      Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + interval, 1),
    );
  }
  // Debug: log for February
  if (occurrences.includes("2022-02") || occurrences.includes("2023-02")) {
    logger.info("Expense covers February:", { expense, occurrences });
  }
  return occurrences;
}

// Add periods per year mapping for normalization
const PERIODS_PER_YEAR = {
  monthly: 12,
  quarterly: 4,
  yearly: 1,
};

// Helper to normalize an amount to the selected period (applies to all records)
function normalizeAmount(amount, recordFreq, periodType) {
  // recordFreq: frequency_multiplier from DB (1=annual, 4=quarterly, 12=monthly)
  // periodType: 'monthly', 'quarterly', 'yearly'
  if (!amount || !recordFreq) return 0;
  const amt = parseFloat(amount);
  if (isNaN(amt)) return 0;
  if (recordFreq === 1) {
    // annual
    if (periodType === "monthly") return amt / 12;
    if (periodType === "quarterly") return amt / 4;
    return amt; // yearly
  } else if (recordFreq === 4) {
    // quarterly
    if (periodType === "monthly") return amt / 3;
    if (periodType === "yearly") return amt * 4;
    return amt; // quarterly
  } else if (recordFreq === 12) {
    // monthly
    if (periodType === "quarterly") return amt * 3;
    if (periodType === "yearly") return amt * 12;
    return amt; // monthly
  } else {
    // Fallback: scale to annual, then to period
    const annual = amt * recordFreq;
    if (periodType === "monthly") return annual / 12;
    if (periodType === "quarterly") return annual / 4;
    return annual; // yearly
  }
}

const ExpensesBudgets = () => {
  // Get context data from parent route
  const { householdId, userId, members = [], onSaveDebts } = useOutletContext();

  // State management using reducer pattern
  const [state, setState] = useState(initialState);
  // Local state for debtsData
  const [debtsData, setDebtsData] = useState([]);
  // Debug log for debtsData
  logger.info("[DEBUG][ExpensesBudgets] debtsData:", debtsData);

  // Memoized values
  const netAmount = useMemo(
    () => state.totalIncome - state.totalExpenses,
    [state.totalIncome, state.totalExpenses],
  );

  // Add state for trend range and history
  const [trendRange, setTrendRange] = useState("1y");

  const monthlyTrendData = useMemo(() => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    return months.map((m) => ({
      name: m,
      Income: state.totalIncome,
      Expenses: state.totalExpenses,
    }));
  }, [state.totalIncome, state.totalExpenses]);

  const categoryDistributionData = useMemo(() => {
    const fixed = state.fixedExpenses.reduce(
      (sum, exp) =>
        sum +
        normalizeToView(exp.amount, exp.frequency_multiplier, state.dateRange),
      0,
    );
    const discretionary = state.discretionaryExpenses.reduce(
      (sum, exp) =>
        sum +
        normalizeToView(exp.amount, exp.frequency_multiplier, state.dateRange),
      0,
    );
    const debtDivisor = getDebtDivisor(state.dateRange);
    const debt = state.hasDebts
      ? debtsData
          .filter((debt) => !debt.deleted)
          .reduce(
            (sum, debt) =>
              sum +
              debtDivisor *
                parseFloat(debt.actual_payment || debt.minimum_payment || 0),
            0,
          )
      : 0;
    return [
      { name: "Fixed Expenses", value: fixed },
      { name: "Discretionary", value: discretionary },
      { name: "Debt Payments", value: debt },
    ];
  }, [
    state.fixedExpenses,
    state.discretionaryExpenses,
    state.hasDebts,
    debtsData,
    state.dateRange,
  ]);

  // Memoized handlers
  const handleAddFixedExpense = useCallback(() => {
    setState((prev) => ({
      ...prev,
      fixedExpenses: [
        ...prev.fixedExpenses,
        {
          expense_type_id: "",
          amount: 0,
          frequency_id: "",
          endDate: "",
          notes: "",
        },
      ],
    }));
  }, []);

  const handleAddDiscretionaryExpense = useCallback(() => {
    setState((prev) => ({
      ...prev,
      discretionaryExpenses: [
        ...prev.discretionaryExpenses,
        { expense_type_id: "", amount: 0, frequency_id: "", notes: "" },
      ],
    }));
  }, []);

  const handleRemoveFixedExpense = useCallback((index) => {
    setState((prev) => ({
      ...prev,
      fixedExpenses: prev.fixedExpenses.filter((_, i) => i !== index),
    }));
  }, []);

  const handleRemoveDiscretionaryExpense = useCallback((index) => {
    setState((prev) => ({
      ...prev,
      discretionaryExpenses: prev.discretionaryExpenses.filter(
        (_, i) => i !== index,
      ),
    }));
  }, []);

  const handleUpdateFixedExpense = useCallback((index, key, value) => {
    setState((prev) => ({
      ...prev,
      fixedExpenses: prev.fixedExpenses.map((exp, i) =>
        i === index ? { ...exp, [key]: value } : exp,
      ),
    }));
  }, []);

  const handleUpdateDiscretionaryExpense = useCallback((index, key, value) => {
    setState((prev) => ({
      ...prev,
      discretionaryExpenses: prev.discretionaryExpenses.map((exp, i) =>
        i === index ? { ...exp, [key]: value } : exp,
      ),
    }));
  }, []);

  const handleSaveDebts = useCallback(
    (updatedDebts) => {
      logger.info("ExpensesBudgets saving debts:", updatedDebts);
      if (onSaveDebts) {
        onSaveDebts(updatedDebts);
      }
    },
    [onSaveDebts],
  );

  const handleStartIntegration = useCallback(() => {
    setState((prev) => ({ ...prev, showIntegrationModal: true }));
  }, []);

  const handleIntegrationProceed = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      showIntegrationModal: false,
      integrationStatus: "connecting",
    }));

    try {
      // TODO: Implement Plaid Link initialization
      logger.info("Starting Plaid integration...");

      // Simulate success for now
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setState((prev) => ({
        ...prev,
        integrationStatus: "connected",
        showBankingPrompt: false,
      }));
    } catch (error) {
      logger.error("Integration failed:", error);
      setState((prev) => ({ ...prev, integrationStatus: "failed" }));
    }
  }, []);

  const handleSave = useCallback(() => {
    const payload = {
      householdId,
      userId,
      fixedExpenses: state.fixedExpenses,
      discretionaryExpenses: state.discretionaryExpenses,
      hasDebts: state.hasDebts,
      totalIncome: state.totalIncome,
      totalExpenses: state.totalExpenses,
      dateRange: state.dateRange,
    };

    logger.info("Saving to backend:", payload);
    alert("Data saved successfully!");

    // Here you would typically send this data to your API
    // Example: await api.saveExpensesBudgets(payload);
  }, [householdId, userId, state]);

  // Fetch data
  useEffect(() => {
    const fetchExpensesData = async () => {
      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/expenses-budgets/behaviors?userId=${userId}`,
        );
        // Split expenses by behavior
        const fixedExpenses = response.data.filter(
          (expense) => expense.is_fixed,
        );
        const discretionaryExpenses = response.data.filter(
          (expense) => expense.is_discretionary,
        );
        // Calculate totals (normalized to selected view)
        const fixedTotal = fixedExpenses.reduce(
          (sum, exp) =>
            sum +
            normalizeToView(
              exp.amount,
              exp.frequency_multiplier,
              state.dateRange,
            ),
          0,
        );
        const discretionaryTotal = discretionaryExpenses.reduce(
          (sum, exp) =>
            sum +
            normalizeToView(
              exp.amount,
              exp.frequency_multiplier,
              state.dateRange,
            ),
          0,
        );
        const debtDivisor = getDebtDivisor(state.dateRange);
        const debtTotal = state.hasDebts
          ? debtsData
              .filter((debt) => !debt.deleted)
              .reduce(
                (sum, debt) =>
                  sum +
                  debtDivisor *
                    parseFloat(
                      debt.actual_payment || debt.minimum_payment || 0,
                    ),
                0,
              )
          : 0;
        const totalExpenses = fixedTotal + discretionaryTotal + debtTotal;
        setState((prev) => ({
          ...prev,
          fixedExpenses,
          discretionaryExpenses,
          totalExpenses,
          loading: false,
        }));
      } catch (error) {
        logger.error("Error fetching expenses data:", error);
        setState((prev) => ({
          ...prev,
          error: "Failed to load expenses data",
          loading: false,
        }));
      }
    };
    if (householdId) {
      fetchExpensesData();
    }
  }, [householdId, userId, debtsData, state.hasDebts, state.dateRange]);

  // Fetch expense types
  useEffect(() => {
    const fetchExpenseTypes = async () => {
      try {
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/expenses-budgets/types?userId=${userId}`,
        );
        setState((prev) => ({ ...prev, expenseTypes: response.data }));
      } catch (error) {
        logger.error("Error fetching expense types:", error);
        // Don't set error state here as it's not critical
      }
    };

    if (householdId) {
      fetchExpenseTypes();
    }
  }, [householdId, userId]);

  // Fetch expense categories
  useEffect(() => {
    const fetchExpenseCategories = async () => {
      try {
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/expenses-budgets/categories?userId=${userId}`,
        );
        setState((prev) => ({ ...prev, expenseCategories: response.data }));
      } catch (error) {
        logger.error("Error fetching expense categories:", error);
        // Don't set error state here as it's not critical
      }
    };

    if (householdId) {
      fetchExpenseCategories();
    }
  }, [householdId, userId]);

  // Fetch expense frequencies
  useEffect(() => {
    const fetchFrequencies = async () => {
      try {
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/expenses-budgets/frequencies?userId=${userId}`,
        );
        setState((prev) => ({ ...prev, frequencies: response.data }));
        logger.info("Fetched frequencies:", response.data);
      } catch (error) {
        logger.error("Error fetching frequencies:", error);
        // Don't set error state here as it's not critical
      }
    };
    if (householdId) {
      fetchFrequencies();
    }
  }, [householdId, userId]);

  // Fetch historical data on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/expenses-budgets/all-with-history/${householdId}?userId=${userId}`,
        );
        logger.info("API response for all-with-history:", response.data);
        setState((prev) => ({
          ...prev,
          historyExpenses: response.data.history,
          loading: false,
        }));
      } catch (error) {
        logger.error("Error fetching historical expenses:", error);
        setState((prev) => ({
          ...prev,
          error: "Failed to load historical expenses",
          loading: false,
        }));
      }
    };
    if (householdId) fetchHistory();
  }, [householdId, userId]);

  // Debug: log fetched historyExpenses and synthetic records
  useEffect(() => {
    logger.info("Fetched historyExpenses:", state.historyExpenses);
    if (state.historyExpenses && state.historyExpenses.length > 0) {
      const synthetic = state.historyExpenses.filter(
        (e) => e.notes && e.notes.includes("SYNTHETIC_TEST"),
      );
      logger.info("SYNTHETIC_TEST records:", synthetic);
      if (synthetic.length > 0) {
        synthetic.forEach((e) => {
          logger.info("Synthetic record:", e);
        });
      }
    }
  }, [state.historyExpenses]);

  // Helper to get the date range for the trend
  const getTrendDateRange = useCallback(() => {
    const now = new Date();
    let start, end;
    if (trendRange === "6m") {
      start = addMonthsToDate(now, -5); // last 6 months including current
      end = now;
    } else if (trendRange === "1y") {
      if (state.dateRange === "yearly") {
        // For yearly view, show only the current year (tax year)
        start = new Date(now.getFullYear(), 0, 1);
        end = now;
      } else {
        start = addMonthsToDate(now, -11); // last 12 months
        end = now;
      }
    } else if (trendRange === "3y") {
      if (state.dateRange === "yearly") {
        // For yearly view, show 3 most recent years ending with current year
        start = new Date(now.getFullYear() - 2, 0, 1);
        end = now;
      } else {
        start = addMonthsToDate(now, -35); // last 36 months
        end = now;
      }
    } else {
      // Since inception: use earliest year with nonzero data and valid start_date
      const allExpenseYears = state.historyExpenses
        .filter((e) => parseFloat(e.amount) > 0 && e.start_date)
        .map((e) => new Date(e.start_date).getFullYear());
      const allIncomeYears = state.incomeHistory
        .filter(
          (e) => parseFloat(e.gross_amount || e.amount) > 0 && e.start_date,
        )
        .map((e) => new Date(e.start_date).getFullYear());
      const allYears = [...allExpenseYears, ...allIncomeYears].filter(
        (y) => y && y > 1900,
      );
      // Debug logs
      logger.info("DEBUG: allExpenseYears:", allExpenseYears);
      logger.info("DEBUG: allIncomeYears:", allIncomeYears);
      logger.info("DEBUG: allYears:", allYears);
      // Log all records for manual inspection
      logger.info("DEBUG: historyExpenses:", state.historyExpenses);
      logger.info("DEBUG: incomeHistory:", state.incomeHistory);
      // Log any record with a start_date in 2021
      state.historyExpenses.forEach((e) => {
        if (e.start_date && new Date(e.start_date).getFullYear() === 2021) {
          logger.info("DEBUG: Expense with 2021 start_date:", e);
        }
      });
      state.incomeHistory.forEach((e) => {
        if (e.start_date && new Date(e.start_date).getFullYear() === 2021) {
          logger.info("DEBUG: Income with 2021 start_date:", e);
        }
      });
      const minYear = allYears.length
        ? Math.min(...allYears)
        : now.getFullYear();
      logger.info("DEBUG: minYear:", minYear);
      start = new Date(minYear, 0, 1);
      end = now;
    }
    return { start, end };
  }, [trendRange, state.dateRange, state.historyExpenses, state.incomeHistory]);

  // Generate period keys for the trend (month, quarter, year)
  function getPeriodRange(start, end, dateRange) {
    const periods = [];
    const d = new Date(start);
    if (dateRange === "yearly") {
      // Ensure we include both start and end years
      let year = d.getFullYear();
      const endYear = end.getFullYear();
      while (year <= endYear) {
        periods.push(`${year}`);
        year++;
      }
    } else {
      while (d <= end) {
        let key;
        if (dateRange === "monthly") {
          key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          d.setMonth(d.getMonth() + 1);
        } else if (dateRange === "quarterly") {
          const quarter = Math.floor(d.getMonth() / 3) + 1;
          key = `${d.getFullYear()}-Q${quarter}`;
          d.setMonth(d.getMonth() + 3);
        }
        periods.push(key);
      }
    }
    return periods;
  }

  // Helper to check if a record is active in a period
  function isActiveInPeriod(record, periodKey, dateRange) {
    const start = record.start_date ? new Date(record.start_date) : null;
    const end = record.end_date ? new Date(record.end_date) : null;
    if (!start) return false;
    let periodStart, periodEnd;
    if (dateRange === "monthly") {
      const [year, month] = periodKey.split("-");
      periodStart = new Date(
        Date.UTC(Number(year), Number(month) - 1, 1, 0, 0, 0),
      );
      const lastDay = new Date(Date.UTC(Number(year), Number(month), 0));
      periodEnd = new Date(
        Date.UTC(
          lastDay.getUTCFullYear(),
          lastDay.getUTCMonth(),
          lastDay.getUTCDate(),
          23,
          59,
          59,
          999,
        ),
      );
    } else if (dateRange === "quarterly") {
      const [year, q] = periodKey.split("-Q");
      const quarter = Number(q);
      periodStart = new Date(
        Date.UTC(Number(year), (quarter - 1) * 3, 1, 0, 0, 0),
      );
      const lastMonth = quarter * 3;
      const lastDay = new Date(Date.UTC(Number(year), lastMonth, 0));
      periodEnd = new Date(
        Date.UTC(
          lastDay.getUTCFullYear(),
          lastDay.getUTCMonth(),
          lastDay.getUTCDate(),
          23,
          59,
          59,
          999,
        ),
      );
    } else {
      periodStart = new Date(Date.UTC(Number(periodKey), 0, 1, 0, 0, 0));
      periodEnd = new Date(
        Date.UTC(Number(periodKey), 11, 31, 23, 59, 59, 999),
      );
    }
    return (!end || end >= periodStart) && start <= periodEnd;
  }

  // Helper to check if a period is a full year
  function isFullYearPeriod(periodKey, dateRange, start, end) {
    if (dateRange !== "yearly") return true;
    const year = parseInt(periodKey);
    const jan1 = new Date(year, 0, 1);
    const dec31 = new Date(year, 11, 31);
    // If the period is outside the data range, it's not a full year
    return jan1 >= start && dec31 <= end;
  }

  // Fetch historical and current expenses and merge for trend chart
  useEffect(() => {
    const fetchAllExpenses = async () => {
      if (!householdId) return;
      try {
        // Fetch both current and history
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/expenses-budgets/all-with-history/${householdId}`,
        );
        // Merge both arrays for trend chart
        setState((prev) => ({
          ...prev,
          allExpenses: [
            ...(response.data.history || []),
            ...(response.data.current || []),
          ],
        }));
      } catch (error) {
        logger.error("Error fetching all expenses:", error);
      }
    };
    fetchAllExpenses();
  }, [householdId]);

  // Helper to get the latest record for each business key active in a period
  function getLatestActiveRecords(
    records,
    periodKey,
    dateRange,
    keyField,
    dateField,
  ) {
    // Group by business key
    const grouped = {};
    for (const rec of records) {
      const key = rec[keyField];
      if (!key) continue;
      if (!isActiveInPeriod(rec, periodKey, dateRange)) continue;
      if (
        !grouped[key] ||
        new Date(rec[dateField] || rec.end_date || rec.updated_at) >
          new Date(
            grouped[key][dateField] ||
              grouped[key].end_date ||
              grouped[key].updated_at,
          )
      ) {
        grouped[key] = rec;
      }
    }
    return Object.values(grouped);
  }

  // Map all records in allExpenses to ensure correct fields and consistent business key
  function mapExpenseRecord(rec) {
    // Defensive: always use the backend's frequency_multiplier, but if frequency_id is present, correct it if needed
    let freqMult = rec.frequency_multiplier;
    if (rec.frequency_id && state.frequencies && state.frequencies.length > 0) {
      const freqObj = state.frequencies.find(
        (f) => f.frequency_id === rec.frequency_id,
      );
      if (freqObj) {
        freqMult = freqObj.frequency_multiplier;
      }
    }
    return {
      ...rec,
      amount:
        rec.amount !== undefined
          ? rec.amount
          : rec.expense_amount !== undefined
            ? rec.expense_amount
            : 0,
      frequency_multiplier: freqMult !== undefined ? freqMult : 1,
      expense_id: rec.expense_id || rec.client_expense_id || rec.id, // always use expense_id as business key
      updated_at: rec.updated_at || rec.end_date || rec.created_at,
    };
  }

  // Generate trend data for the selected period granularity
  const monthlyTrendDataFromHistory = useMemo(() => {
    if (!state.allExpenses || !state.incomeHistory) return [];
    const { start, end } = getTrendDateRange();
    let periods;

    // 1. First, deduplicate income records by income_id
    const incomeRecordMap = new Map();
    // Insert all records, letting "current" overwrite any duplicate from "history"
    (state.incomeHistory || []).forEach((inc) => {
      const key = inc.income_id ?? inc.client_income_id;
      if (key != null) {
        incomeRecordMap.set(key, inc);
      }
    });
    // Get the deduplicated array of income records
    const allUniqueIncomeRows = Array.from(incomeRecordMap.values());

    // Special handling for yearly view and 'Since Inception'
    if (state.dateRange === "yearly" && trendRange === "all") {
      // Only allow years 2022 and later
      const expenseYears = state.allExpenses
        .filter(
          (e) =>
            parseFloat(e.amount) > 0 &&
            e.start_date &&
            new Date(e.start_date).getFullYear() >= 2022,
        )
        .map((e) => new Date(e.start_date).getFullYear());
      const incomeYears = allUniqueIncomeRows // Use deduplicated income records here
        .filter(
          (e) =>
            parseFloat(e.gross_amount || e.amount) > 0 &&
            e.start_date &&
            new Date(e.start_date).getFullYear() >= 2022,
        )
        .map((e) => new Date(e.start_date).getFullYear());
      const yearsWithData = Array.from(
        new Set([...expenseYears, ...incomeYears]),
      );
      // Always include the current year if not present
      const currentYear = new Date().getFullYear();
      if (!yearsWithData.includes(currentYear)) {
        yearsWithData.push(currentYear);
      }
      yearsWithData.sort((a, b) => a - b);
      // Remove any year before 2022
      periods = yearsWithData.filter((y) => y >= 2022).map(String);
    } else {
      periods = getPeriodRange(start, end, state.dateRange);
    }
    const mappedExpenses = state.allExpenses.map(mapExpenseRecord);
    // Determine the current period key
    const now = new Date();
    let currentPeriodKey;
    if (state.dateRange === "monthly") {
      currentPeriodKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    } else if (state.dateRange === "quarterly") {
      const quarter = Math.floor(now.getMonth() / 3) + 1;
      currentPeriodKey = `${now.getFullYear()}-Q${quarter}`;
    } else {
      currentPeriodKey = `${now.getFullYear()}`;
    }
    // Filter periods: only include those with at least one active income or expense, or the current period
    const filteredPeriods = periods.filter((periodKey) => {
      // Calculate period start and end
      let periodStart, periodEnd;
      if (state.dateRange === "monthly") {
        const [year, month] = periodKey.split("-");
        periodStart = new Date(
          Date.UTC(Number(year), Number(month) - 1, 1, 0, 0, 0),
        );
        const lastDay = new Date(Date.UTC(Number(year), Number(month), 0));
        periodEnd = new Date(
          Date.UTC(
            lastDay.getUTCFullYear(),
            lastDay.getUTCMonth(),
            lastDay.getUTCDate(),
            23,
            59,
            59,
            999,
          ),
        );
      } else if (state.dateRange === "quarterly") {
        const [year, q] = periodKey.split("-Q");
        const quarter = Number(q);
        periodStart = new Date(
          Date.UTC(Number(year), (quarter - 1) * 3, 1, 0, 0, 0),
        );
        const lastMonth = quarter * 3;
        const lastDay = new Date(Date.UTC(Number(year), lastMonth, 0));
        periodEnd = new Date(
          Date.UTC(
            lastDay.getUTCFullYear(),
            lastDay.getUTCMonth(),
            lastDay.getUTCDate(),
            23,
            59,
            59,
            999,
          ),
        );
      } else {
        periodStart = new Date(Date.UTC(Number(periodKey), 0, 1, 0, 0, 0));
        periodEnd = new Date(
          Date.UTC(Number(periodKey), 11, 31, 23, 59, 59, 999),
        );
      }
      // Only include if there is a nonzero amount for income or expense, or if it is the current period
      const hasActiveExpense = mappedExpenses.some((exp) => {
        return (
          new Date(exp.start_date) <= periodEnd &&
          (!exp.end_date || new Date(exp.end_date) >= periodStart) &&
          parseFloat(exp.amount) > 0
        );
      });
      const hasActiveIncome = allUniqueIncomeRows.some((inc) => {
        // Use deduplicated income records here
        const recStart = new Date(inc.start_date);
        const recEnd = inc.end_date ? new Date(inc.end_date) : null;
        return (
          recStart <= periodEnd &&
          (!recEnd || recEnd >= periodStart) &&
          parseFloat(inc.gross_amount || inc.amount) > 0
        );
      });
      return (
        hasActiveExpense || hasActiveIncome || periodKey === currentPeriodKey
      );
    });
    // After filtering, always include the current year for yearly view and 'Since Inception' trend range
    if (state.dateRange === "yearly" && trendRange === "all") {
      const currentYear = `${new Date().getFullYear()}`;
      if (!filteredPeriods.includes(currentYear)) {
        filteredPeriods.push(currentYear);
      }
    }
    // Sort periods numerically for yearly view
    if (state.dateRange === "yearly") {
      filteredPeriods.sort((a, b) => parseInt(a) - parseInt(b));
    }
    return filteredPeriods.map((periodKey) => {
      // Calculate period start and end
      let periodStart, periodEnd;
      if (state.dateRange === "monthly") {
        const [year, month] = periodKey.split("-");
        periodStart = new Date(
          Date.UTC(Number(year), Number(month) - 1, 1, 0, 0, 0),
        );
        const lastDay = new Date(Date.UTC(Number(year), Number(month), 0));
        periodEnd = new Date(
          Date.UTC(
            lastDay.getUTCFullYear(),
            lastDay.getUTCMonth(),
            lastDay.getUTCDate(),
            23,
            59,
            59,
            999,
          ),
        );
      } else if (state.dateRange === "quarterly") {
        const [year, q] = periodKey.split("-Q");
        const quarter = Number(q);
        periodStart = new Date(
          Date.UTC(Number(year), (quarter - 1) * 3, 1, 0, 0, 0),
        );
        const lastMonth = quarter * 3;
        const lastDay = new Date(Date.UTC(Number(year), lastMonth, 0));
        periodEnd = new Date(
          Date.UTC(
            lastDay.getUTCFullYear(),
            lastDay.getUTCMonth(),
            lastDay.getUTCDate(),
            23,
            59,
            59,
            999,
          ),
        );
      } else {
        periodStart = new Date(Date.UTC(Number(periodKey), 0, 1, 0, 0, 0));
        periodEnd = new Date(
          Date.UTC(Number(periodKey), 11, 31, 23, 59, 59, 999),
        );
      }
      // Deduplicate by logical key (user_id + type_id) and pick the record with latest start_date covering the period
      const logicalKey = (rec) =>
        `${rec.user_id || ""}_${rec.expense_type_id || rec.income_type_id || ""}`;
      const logicalExpenseMap = {};
      for (const exp of mappedExpenses) {
        // Strictest for yearly view: only include if record is active in that year and amount > 0
        if (
          state.dateRange === "yearly"
            ? parseFloat(exp.amount) > 0 &&
              new Date(exp.start_date).getFullYear() <= Number(periodKey) &&
              (!exp.end_date ||
                new Date(exp.end_date).getFullYear() >= Number(periodKey))
            : new Date(exp.start_date) <= periodEnd &&
              (!exp.end_date || new Date(exp.end_date) >= periodStart)
        ) {
          const key = logicalKey(exp);
          if (
            !logicalExpenseMap[key] ||
            new Date(exp.start_date) >
              new Date(logicalExpenseMap[key].start_date)
          ) {
            logicalExpenseMap[key] = exp;
          }
        }
      }
      // Debug: log deduplicated logical keys and records
      logger.info(
        `[LOGICAL DEDUP] Period: ${periodKey} - Logical Expense Keys:`,
        Object.keys(logicalExpenseMap),
      );
      logger.info(
        `[LOGICAL DEDUP] Period: ${periodKey} - Logical Expenses:`,
        Object.values(logicalExpenseMap),
      );
      let totalExpenses = 0;
      for (const key in logicalExpenseMap) {
        const exp = logicalExpenseMap[key];
        const freqMult = exp.frequency_multiplier || 1;
        const normalized = normalizeAmount(
          exp.amount,
          freqMult,
          state.dateRange,
        );
        totalExpenses += isNaN(normalized) ? 0 : normalized;
      }
      // Debt payments for this period
      let debtTotal = 0;
      if (state.hasDebts && Array.isArray(debtsData)) {
        for (const debt of debtsData) {
          if (debt.deleted) continue;
          const debtDivisor =
            state.dateRange === "quarterly"
              ? 3
              : state.dateRange === "yearly"
                ? 12
                : 1;
          const payment = parseFloat(
            debt.actual_payment || debt.minimum_payment || 0,
          );
          debtTotal += debtDivisor * payment;
        }
      }
      totalExpenses += debtTotal;

      // First deduplicate by income_id to prevent history+current duplicates
      const incomeRecordMap = new Map();
      state.incomeHistory.forEach((inc) => {
        const key = inc.income_id ?? inc.client_income_id;
        if (key != null) {
          incomeRecordMap.set(key, inc);
        }
      });
      const uniqueIncomes = Array.from(incomeRecordMap.values());

      // Now calculate total income for the period - sum ALL unique incomes
      let totalIncome = 0;
      for (const inc of uniqueIncomes) {
        const recStart = new Date(inc.start_date);
        const recEnd = inc.end_date ? new Date(inc.end_date) : null;

        // If income is active in this period, add it to the total
        if (recStart <= periodEnd && (!recEnd || recEnd >= periodStart)) {
          const freqMult = inc.frequency_multiplier || 1;
          const normalized = normalizeAmount(
            inc.gross_amount || inc.amount,
            freqMult,
            state.dateRange,
          );
          if (periodKey.endsWith("-12")) {
            logger.info(`[DEBUG] Including income:`, {
              income_id: inc.income_id,
              start: recStart.toISOString(),
              end: recEnd?.toISOString(),
              amount: inc.gross_amount || inc.amount,
              freqMult,
              normalized,
            });
          }
          totalIncome += isNaN(normalized) ? 0 : normalized;
        }
      }

      let name = periodKey;
      if (state.dateRange === "yearly") {
        const currentYear = new Date().getFullYear();
        if (parseInt(periodKey) === currentYear) {
          // If the current year is not complete, show YTD
          const now = new Date();
          const isFullYear = now.getMonth() === 11 && now.getDate() === 31;
          if (!isFullYear) {
            name = `${periodKey} (YTD)`;
          }
        }
      }
      return {
        name,
        Income: totalIncome,
        Expenses: totalExpenses,
      };
    });
  }, [
    state.allExpenses,
    state.incomeHistory,
    getTrendDateRange,
    state.dateRange,
    debtsData,
    state.hasDebts,
  ]);

  // Fetch income history for trend chart
  useEffect(() => {
    const fetchIncomeHistory = async () => {
      if (!householdId) return;
      try {
        const response = await api.get(
          `/advisor-portal/income-benefits/all-with-history/${householdId}?userId=${userId}`,
        );

        // Deduplicate by income_id before storing in state
        const recordMap = new Map();

        // First add history records
        (response.data.history || []).forEach((inc) => {
          const key = inc.income_id ?? inc.client_income_id;
          if (key != null) {
            recordMap.set(key, inc);
          }
        });

        // Then add current records, overwriting any duplicates
        (response.data.current || []).forEach((inc) => {
          const key = inc.income_id ?? inc.client_income_id;
          if (key != null) {
            recordMap.set(key, inc);
          }
        });

        // Convert back to array
        const deduplicatedIncome = Array.from(recordMap.values());

        setState((prev) => ({
          ...prev,
          incomeHistory: deduplicatedIncome,
        }));
      } catch (error) {
        logger.error("Error fetching income history:", error);
      }
    };
    fetchIncomeHistory();
  }, [householdId, userId]);

  // Calculate expenses by category (normalized to selected view)
  const expensesByCategory = useMemo(() => {
    const allExpenses = [
      ...state.fixedExpenses,
      ...state.discretionaryExpenses,
    ];
    const categoryTotals = {};
    allExpenses.forEach((expense) => {
      const category = expense.category || "Uncategorized";
      const value = normalizeToView(
        expense.amount,
        expense.frequency_multiplier,
        state.dateRange,
      );
      categoryTotals[category] = (categoryTotals[category] || 0) + value;
    });
    return Object.entries(categoryTotals).map(([name, value]) => ({
      name,
      value,
    }));
  }, [state.fixedExpenses, state.discretionaryExpenses, state.dateRange]);

  // Filter expenses based on selection
  const filterExpenses = useCallback(
    (expenses) => {
      switch (state.expenseFilter) {
        case "joint":
          return expenses.filter((expense) => expense.isJoint);
        case "personal":
          return expenses.filter((expense) => expense.isCurrentUser);
        case "household":
          return expenses.filter((expense) => !expense.isJoint);
        default:
          return expenses;
      }
    },
    [state.expenseFilter],
  );

  // Get expense types by behavior
  const getExpenseTypesByBehavior = useCallback(
    (behavior) => {
      return state.expenseTypes
        .filter((type) => type.ExpenseBehavior?.behavior_name === behavior)
        .map((type) => ({
          value: type.expense_type_id,
          label: type.expense_type_name,
        }));
    },
    [state.expenseTypes],
  );

  // Monthly/Quarterly/Annual Trend label
  const trendLabel = useMemo(() => {
    if (state.dateRange === "quarterly") return "Quarterly Trend";
    if (state.dateRange === "yearly") return "Annual Trend";
    return "Monthly Trend";
  }, [state.dateRange]);

  // Fetch income data for the current year and update totalIncome
  // ────────────────────────────────────────────────────────────────
  // Fetch income data for the current period and update totalIncome
  // (DEDUPE "history" + "current" by income_id before summing)
  useEffect(() => {
    const fetchIncomeData = async () => {
      if (!householdId) return;

      try {
        // 1. Get history + current from the API
        const response = await api.get(
          `/advisor-portal/income-benefits/all-with-history/${householdId}`,
        );
        const historyRecords = response.data.history || [];
        const currentRecords = response.data.current || [];

        // 2. Build a Map to de‐duplicate by income_id (or client_income_id)
        //    If your API uses client_income_id instead of income_id, switch to that key.
        const recordMap = new Map();

        // Insert "history" first:
        historyRecords.forEach((inc) => {
          const key = inc.income_id ?? inc.client_income_id;
          if (key != null) {
            recordMap.set(key, inc);
          }
        });
        // Then insert "current," which will overwrite any duplicate key from history:
        currentRecords.forEach((inc) => {
          const key = inc.income_id ?? inc.client_income_id;
          if (key != null) {
            recordMap.set(key, inc);
          }
        });

        // Now recordMap.values() contains each unique row exactly once.
        const allUniqueRecords = Array.from(recordMap.values());

        // 3. Determine the current period window
        const now = new Date();
        let periodStart, periodEnd;

        if (state.dateRange === "monthly") {
          periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
          periodEnd = new Date(
            now.getFullYear(),
            now.getMonth() + 1,
            0,
            23,
            59,
            59,
            999,
          );
        } else if (state.dateRange === "quarterly") {
          const quarter = Math.floor(now.getMonth() / 3) + 1;
          periodStart = new Date(now.getFullYear(), (quarter - 1) * 3, 1);
          periodEnd = new Date(
            now.getFullYear(),
            quarter * 3,
            0,
            23,
            59,
            59,
            999,
          );
        } else {
          // yearly
          periodStart = new Date(now.getFullYear(), 0, 1);
          periodEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        }

        // 4. Filter for "active in this period"
        const activeRecords = allUniqueRecords.filter((inc) => {
          const recStart = new Date(inc.start_date);
          const recEnd = inc.end_date ? new Date(inc.end_date) : null;
          return recStart <= periodEnd && (!recEnd || recEnd >= periodStart);
        });

        // 5. Normalize every active record and sum
        let totalIncome = 0;
        activeRecords.forEach((inc) => {
          const freqMult = inc.frequency_multiplier || 1;
          // Some payloads put the dollar value in gross_amount, some in amount
          const rawAmount = inc.gross_amount ?? inc.amount ?? 0;
          const normalized = normalizeAmount(
            rawAmount,
            freqMult,
            state.dateRange,
          );
          if (!isNaN(normalized)) {
            totalIncome += normalized;
          }
        });

        // 6. Push the final monthly/quarterly/yearly sum into state
        setState((prev) => ({ ...prev, totalIncome }));
      } catch (error) {
        logger.error("Error fetching income data:", error);
        // We do not set an error state here, but you could if you want:
        // setState(prev => ({ ...prev, error: 'Failed to load income', ... }));
      }
    };

    fetchIncomeData();
  }, [householdId, state.dateRange]);

  // Log the final data passed to the chart
  logger.info("[CHART DATA] monthlyTrendData:", monthlyTrendDataFromHistory);

  // Fetch debts for the household
  useEffect(() => {
    if (!householdId) return;
    const fetchDebts = async () => {
      try {
        const response = await api.get(
          `/advisor-portal/balance-sheet/all-with-history/${householdId}?userId=${userId}`,
        );
        if (
          response.data &&
          response.data.current &&
          Array.isArray(response.data.current.debts)
        ) {
          // Group debts by minimum_payment amount
          const debtMap = new Map();
          response.data.current.debts.forEach(debt => {
            const key = debt.minimum_payment?.toString();
            // If we already have this amount, only replace if current is member-specific and existing is household
            const existing = debtMap.get(key);
            if (!existing || (!debt.user_id && existing.user_id) || (debt.user_id && !existing.user_id)) {
              debtMap.set(key, debt);
            }
          });
          
          // Convert back to array
          const dedupedDebts = Array.from(debtMap.values());
          // console.log('Original debts:', response.data.current.debts);
          // console.log('Deduped debts:', dedupedDebts);
          setDebtsData(dedupedDebts);
        }
      } catch (err) {
        logger.error("[ExpensesBudgets] Failed to fetch debts:", err);
      }
    };
    fetchDebts();
  }, [householdId, userId]);

  // Render loading state
  if (state.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Render error state
  if (state.error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{state.error}</p>
            <button
              onClick={() => setState((prev) => ({ ...prev, error: null }))}
              className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Add debug log before rendering
  logger.info(
    "Rendering ExpensesBudgets: state.currentExpenses:",
    state.currentExpenses,
    "state.fixedExpenses:",
    state.fixedExpenses,
    "state.discretionaryExpenses:",
    state.discretionaryExpenses,
  );
  logger.info(
    "Rendering ExpensesBudgets: state.frequencies:",
    state.frequencies,
  );
  if (!state.frequencies || state.frequencies.length === 0) {
    logger.warn("No frequencies loaded! The dropdowns will be empty.");
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="mb-8 bg-surface-default dark:bg-surface-default shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Expenses & Budgets
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track and manage your household expenses
            </p>
          </div>
          <select
            value={state.dateRange}
            onChange={(e) =>
              setState((prev) => ({ ...prev, dateRange: e.target.value }))
            }
            className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-surface-default dark:bg-surface-default"
          >
            <option value="monthly">Monthly View</option>
            <option value="quarterly">Quarterly View</option>
            <option value="yearly">Yearly View</option>
          </select>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
            <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">
              Total Income
            </h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(state.totalIncome)}
            </p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border border-red-200">
            <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">
              Total Expenses
            </h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(state.totalExpenses)}
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
            <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">
              Net Cash Flow
            </h3>
            <p
              className={`text-2xl font-bold ${netAmount >= 0 ? "text-green-600" : "text-red-600"}`}
            >
              {formatCurrency(netAmount)}
            </p>
          </div>
        </div>
      </header>

      {/* Charts */}
      <div className="bg-surface-default dark:bg-surface-default shadow-sm p-6 mb-8">
        {/* Range Selector */}
        <div className="flex items-center mb-4">
          <span className="mr-2 font-medium">Trend Range:</span>
          <select
            value={trendRange}
            onChange={(e) => setTrendRange(e.target.value)}
            className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-surface-default dark:bg-surface-default"
          >
            {RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <ExpenseCharts
          monthlyTrendData={monthlyTrendDataFromHistory}
          categoryDistributionData={categoryDistributionData}
          expensesByCategory={expensesByCategory}
          formatCurrency={formatCurrency}
          trendLabel={trendLabel}
        />
      </div>

      {/* Expenses Section */}
      <section className="bg-surface-default dark:bg-surface-default shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Expenses</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your fixed and discretionary expenses
            </p>
          </div>
          <select
            value={state.expenseFilter}
            onChange={(e) =>
              setState((prev) => ({ ...prev, expenseFilter: e.target.value }))
            }
            className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-surface-default dark:bg-surface-default"
          >
            <option value="all">All Expenses</option>
            <option value="joint">Joint Expenses</option>
            <option value="personal">My Expenses</option>
            <option value="household">All Household Members</option>
          </select>
        </div>

        {/* Fixed Expenses */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Fixed Monthly Expenses
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Regular, predictable expenses
              </p>
            </div>
            <button
              onClick={handleAddFixedExpense}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors shadow-sm"
            >
              <PlusIcon className="h-5 w-5" />
              Add Fixed Expense
            </button>
          </div>
          {filterExpenses(state.fixedExpenses).map((expense, index) => (
            <ExpenseRow
              key={expense.id || index}
              expense={expense}
              index={index}
              isFixed={true}
              availableTypes={[
                { value: "", label: "Select Type" },
                ...getExpenseTypesByBehavior("Fixed"),
              ]}
              frequencies={state.frequencies}
              onUpdate={handleUpdateFixedExpense}
              onRemove={handleRemoveFixedExpense}
            />
          ))}
        </div>

        {/* Discretionary Expenses */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Discretionary Expenses
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Variable, optional expenses
              </p>
            </div>
            <button
              onClick={handleAddDiscretionaryExpense}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <PlusIcon className="h-5 w-5" />
              Add Discretionary Expense
            </button>
          </div>
          {filterExpenses(state.discretionaryExpenses).map((expense, index) => (
            <ExpenseRow
              key={expense.id || index}
              expense={expense}
              index={index}
              isFixed={false}
              availableTypes={[
                { value: "", label: "Select Type" },
                ...getExpenseTypesByBehavior("Discretionary"),
              ]}
              frequencies={state.frequencies}
              onUpdate={handleUpdateDiscretionaryExpense}
              onRemove={handleRemoveDiscretionaryExpense}
            />
          ))}
        </div>
      </section>

      {/* Debts Section */}
      <section className="bg-surface-default dark:bg-surface-default shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Debt & Loan Obligations
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track your outstanding debts and payments
            </p>
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={state.hasDebts}
              onChange={(e) =>
                setState((prev) => ({ ...prev, hasDebts: e.target.checked }))
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-gray-700 dark:text-gray-300">I have outstanding debts</span>
          </label>
        </div>

        {state.hasDebts && (
          <div className="bg-gray-50 rounded-lg p-4">
            <DebtAnalysisTable
              debtsData={debtsData}
              members={members}
              onSave={handleSaveDebts}
              config={{
                editable: true,
                showDates: false,
                showActualPayment: true,
                showDebtorName: true,
                showPayoffDate: true,
                showLastUpdated: true,
                showMember: true,
              }}
            />
          </div>
        )}
      </section>

      {/* Banking Integration */}
      {state.showBankingPrompt && (
        <section className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 mb-8 border border-blue-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Connect Your Bank Account
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Sync your accounts to auto-track expenses and gain personalized
                insights.
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleStartIntegration}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                Connect Now
              </button>
              <button
                onClick={() =>
                  setState((prev) => ({ ...prev, showBankingPrompt: false }))
                }
                className="bg-surface-default dark:bg-surface-default text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm border border-gray-300"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Save Button */}
      <div className="flex justify-end px-6 pb-6">
        <button
          onClick={handleSave}
          className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors shadow-sm flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          Save Changes
        </button>
      </div>

      {/* Banking Integration Modal */}
      <BankingIntegrationModal
        isOpen={state.showIntegrationModal}
        onClose={() =>
          setState((prev) => ({ ...prev, showIntegrationModal: false }))
        }
        onProceed={handleIntegrationProceed}
      />
    </div>
  );
};

export default ExpensesBudgets;
